import { createClient } from '@supabase/supabase-js';
import { mapProductRow } from './catalogMapping';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseAnonKey) {
  console.error('REACT_APP_SUPABASE_ANON_KEY environment variable is not set. Supabase client will not work correctly.');
}

// SECURITY: Only use the anon key on the client side. Never use service_role key in frontend code.
export const supabase = createClient(supabaseUrl || '', supabaseAnonKey || '', {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
});

const PRODUCT_SELECT = `
  *,
  product_images ( storage_path, position, alt_text, is_marketing )
`;

// RLS already restricts anon to status='active'; the explicit filter keeps
// admin sessions (later phases) from leaking drafts into the storefront.
export const supabaseHelpers = {
  async getProducts({ category, limit } = {}) {
    let query = supabase
      .from('products')
      .select(PRODUCT_SELECT)
      .eq('status', 'active')
      .order('name');
    if (category) query = query.contains('categories', [category]);
    if (limit) query = query.limit(limit);
    const { data, error } = await query;
    if (error) throw new Error(`getProducts failed: ${error.message}`);
    return (data ?? []).map((row) => mapProductRow(row, supabaseUrl));
  },

  async getProduct(idOrSlug) {
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(idOrSlug);
    const { data, error } = await supabase
      .from('products')
      .select(PRODUCT_SELECT)
      .eq('status', 'active')
      .eq(isUuid ? 'id' : 'slug', idOrSlug)
      .maybeSingle();
    if (error) throw new Error(`getProduct failed: ${error.message}`);
    if (!data) return null;
    return mapProductRow(data, supabaseUrl);
  },

  // Variations for the display-only options viewer.
  async getProductVariations(productId) {
    const { data: links, error } = await supabase
      .from('product_option_links')
      .select(`
        custom_title, sort_order, included_value_ids, is_required,
        option:product_options ( id, name )
      `)
      .eq('product_id', productId)
      .order('sort_order');
    if (error) throw new Error(`getProductVariations failed: ${error.message}`);
    if (!links || links.length === 0) return [];

    const allValueIds = links.flatMap((l) => l.included_value_ids ?? []);
    if (allValueIds.length === 0) return [];
    const { data: values, error: valErr } = await supabase
      .from('product_option_values')
      .select('id, title, hex_color, image_url, sort_order, price_modifier')
      .in('id', allValueIds);
    if (valErr) throw new Error(`getProductVariations values failed: ${valErr.message}`);
    const byId = new Map((values ?? []).map((v) => [v.id, v]));

    return links.map((l) => ({
      id: l.option?.id,
      name: l.custom_title || l.option?.name || 'Options',
      is_required: l.is_required ?? false,
      values: (l.included_value_ids ?? [])
        .map((id) => byId.get(id))
        .filter(Boolean)
        .sort((a, b) => a.sort_order - b.sort_order),
    }));
  },

  async searchProducts(query) {
    const { data, error } = await supabase
      .from('products')
      .select(PRODUCT_SELECT)
      .eq('status', 'active')
      .ilike('name', `%${query}%`)
      .order('name');
    if (error) throw new Error(`searchProducts failed: ${error.message}`);
    return (data ?? []).map((row) => mapProductRow(row, supabaseUrl));
  },
};

export default supabase;
